/* ========================================================
   COR · CONTACT — interactive layer
   - Floating labels w/ focus + filled states
   - Scroll-spy sticky side rail
   - Submit loading state + sweep
   - Cinematic success: countdown -> /
   - 3D stage ready handoff (cor-helmet-3d.js auto-mounts)
   - Hard-stops on prefers-reduced-motion
   ======================================================== */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var ROOT_SELECTOR = '[data-cor-contact-root]';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  /* ------------------------------------------------------
     1. Floating labels
        Label sits BEFORE input in the DOM (so screen readers
        announce field name first). We toggle .is-focused /
        .is-filled on the field wrapper.
     ------------------------------------------------------ */
  function initFields(root) {
    var fields = root.querySelectorAll('[data-cor-field]');
    fields.forEach(function (field) {
      var control = field.querySelector('input, select, textarea');
      if (!control) return;

      var sync = function () {
        var v = (control.value || '').trim();
        // <select> is always "filled" because of default option,
        // but visually we still want label floated if a real choice.
        if (control.tagName === 'SELECT') {
          field.classList.add('is-filled');
          return;
        }
        field.classList.toggle('is-filled', v.length > 0);
      };

      control.addEventListener('focus', function () {
        field.classList.add('is-focused');
      });
      control.addEventListener('blur', function () {
        field.classList.remove('is-focused');
        sync();
      });
      control.addEventListener('input', sync);
      control.addEventListener('change', sync);

      // Initial state (handles browser autofill / form re-render)
      sync();
      // Belt-and-suspenders: re-sync after a tick (autofill races)
      setTimeout(sync, 400);
    });
  }

  /* ------------------------------------------------------
     2. Submit loading + form lifecycle
        Shopify contact form is a real POST; once submitted,
        page reloads and Liquid renders the success state.
        We just give a brief loading affordance.
     ------------------------------------------------------ */
  function initSubmit(root) {
    var form = root.querySelector('.cor-contact__form');
    var btn = root.querySelector('[data-cor-contact-submit]');
    if (!form || !btn) return;

    form.addEventListener('submit', function () {
      // Don't preventDefault — let Shopify handle the POST.
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    });
  }

  /* ------------------------------------------------------
     3. Scroll-spy side rail
        Each side block has data-cor-side-id; spy links link
        to those ids. We highlight the block + link nearest
        the top viewport quarter.
     ------------------------------------------------------ */
  function initScrollSpy(root) {
    var blocks = Array.prototype.slice.call(root.querySelectorAll('[data-cor-side-block]'));
    var links  = Array.prototype.slice.call(root.querySelectorAll('[data-cor-spy-link]'));
    if (!blocks.length) return;

    // Smooth-scroll the spy links to their target blocks
    links.forEach(function (link) {
      link.addEventListener('click', function (ev) {
        ev.preventDefault();
        var id = link.getAttribute('data-cor-spy-link');
        var target = root.querySelector('[data-cor-side-id="' + id + '"]');
        if (!target) return;
        target.scrollIntoView({
          behavior: reduce ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });

    var setActive = function (id) {
      blocks.forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-cor-side-id') === id);
      });
      links.forEach(function (l) {
        l.classList.toggle('is-active', l.getAttribute('data-cor-spy-link') === id);
      });
    };

    if (!('IntersectionObserver' in window)) {
      setActive(blocks[0].getAttribute('data-cor-side-id'));
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      // Pick the most-visible entry
      var visible = entries
        .filter(function (e) { return e.isIntersecting; })
        .sort(function (a, b) { return b.intersectionRatio - a.intersectionRatio; });
      if (visible.length) {
        setActive(visible[0].target.getAttribute('data-cor-side-id'));
      }
    }, {
      rootMargin: '-25% 0px -45% 0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });

    blocks.forEach(function (b) { io.observe(b); });
    setActive(blocks[0].getAttribute('data-cor-side-id'));
  }

  /* ------------------------------------------------------
     4. SLA counter ramp (24-hour pulse)
        Animates 0 → target once in view. Stable, frame-rate
        independent via performance.now().
     ------------------------------------------------------ */
  function initCounters(root) {
    if (reduce) {
      root.querySelectorAll('[data-cor-counter]').forEach(function (el) {
        el.textContent = el.getAttribute('data-cor-counter-target') || el.textContent;
      });
      return;
    }
    var nodes = root.querySelectorAll('[data-cor-counter]');
    if (!nodes.length || !('IntersectionObserver' in window)) return;

    var animate = function (el) {
      var target = parseInt(el.getAttribute('data-cor-counter-target') || '0', 10);
      if (!target) { el.textContent = el.getAttribute('data-cor-counter-target') || el.textContent; return; }
      var dur = 1200;
      var t0 = performance.now();
      var tick = function (now) {
        var t = Math.min(1, (now - t0) / dur);
        // ease-out cubic
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = String(Math.round(eased * target));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          animate(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.6 });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ------------------------------------------------------
     5. Success countdown -> redirect
     ------------------------------------------------------ */
  function initSuccessCountdown(root) {
    var box = root.querySelector('[data-cor-contact-success]');
    if (!box) return;
    var seconds = box.querySelector('[data-cor-success-seconds]');
    if (!seconds) return;

    // WCAG 4.1.3: the per-second countdown value lives inside the
    // role="status" aria-live="polite" success region. Announcing the
    // changing number every second would flood AT and bury the "thank you".
    // Hide ONLY the changing value from AT (the surrounding sentence stays
    // readable, and a one-time cancellation message below can still be
    // politely announced).
    seconds.setAttribute('aria-hidden', 'true');

    // WCAG 2.2.1: do not impose a time limit on reduced-motion users.
    // Bail out of the auto-redirect entirely; the visible action links remain.
    if (reduce) return;

    var n = parseInt(seconds.textContent, 10);
    if (isNaN(n) || n <= 0) n = 15;
    seconds.textContent = String(n);

    var tickId = null;
    var stopped = false;
    var tick = function () {
      n -= 1;
      if (n <= 0) {
        window.clearInterval(tickId);
        tickId = null;
        try { window.location.href = '/'; } catch (e) { /* noop */ }
        return;
      }
      seconds.textContent = String(n);
    };
    tickId = window.setInterval(tick, 1000);

    // WCAG 2.2.1 + 1.4.13: cancel the auto-redirect on interaction AND on
    // focus/hover entering the success region, and surface the cancelled
    // state with explicit bilingual TEXT (not opacity alone) so it is both
    // announced and readable. corApplyLanguage swaps via data-cor-en/he.
    var cancel = function () {
      if (stopped) return;
      stopped = true;
      if (tickId) { window.clearInterval(tickId); tickId = null; }
      var msg = box.querySelector('.cor-contact__success-meta');
      if (!msg) return;
      msg.setAttribute('data-cor-en', 'Auto-redirect cancelled.');
      msg.setAttribute('data-cor-he', 'ההפניה האוטומטית בוטלה.');
      // Apply immediately for whichever language is active (Hebrew is the
      // authoritative `cor-lang-he` class, mirrored by theme.liquid).
      var isHe = document.documentElement.classList.contains('cor-lang-he');
      msg.textContent = isHe ? 'ההפניה האוטומטית בוטלה.' : 'Auto-redirect cancelled.';
      // Re-run the theme localizer so the new bilingual attrs are honored
      // (mirrors the existing convention; pass the active language).
      if (typeof window.corApplyLanguage === 'function') {
        try { window.corApplyLanguage(isHe ? 'he' : 'en'); } catch (e) { /* noop */ }
      }
    };
    box.addEventListener('click', cancel, { once: true });
    box.addEventListener('mouseenter', cancel, { once: true });
    box.addEventListener('focusin', cancel, { once: true });
    window.addEventListener('keydown', cancel, { once: true });

    // Scroll the success view into focus
    if (!reduce && box.scrollIntoView) {
      window.setTimeout(function () {
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
    }
  }

  /* ------------------------------------------------------
     6. 3D stage ready handoff
        cor-helmet-3d.js mounts a <canvas> inside the
        [data-cor-helmet] target. We watch for it and add
        .is-ready so the static SVG fallback fades out.
     ------------------------------------------------------ */
  function initStage(root) {
    var stage = root.querySelector('.cor-contact__stage');
    if (!stage) return;

    if (reduce) {
      // Reduced motion: don't even bother mounting; CSS forces fallback.
      // Strip the auto-mount hook so cor-helmet-3d.js skips it.
      var target = stage.querySelector('[data-cor-helmet]');
      if (target) target.removeAttribute('data-cor-helmet');
      return;
    }

    var target = stage.querySelector('[data-cor-helmet]');
    if (!target) return;

    var markReady = function () {
      if (target.querySelector('canvas')) {
        stage.classList.add('is-ready');
        return true;
      }
      return false;
    };

    if (markReady()) return;

    // Watch for the renderer to inject its canvas
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        if (markReady()) mo.disconnect();
      });
      mo.observe(target, { childList: true, subtree: true });
      // Safety timeout — bail after 6s
      window.setTimeout(function () { mo.disconnect(); }, 6000);
    } else {
      // Poll fallback
      var tries = 0;
      var iv = window.setInterval(function () {
        tries += 1;
        if (markReady() || tries > 60) window.clearInterval(iv);
      }, 100);
    }

    // Subtle parallax: nudge the stage on cursor for depth.
    // Disabled on coarse pointers.
    var coarse = window.matchMedia && window.matchMedia('(hover: none)').matches;
    if (coarse) return;

    var canvasWrap = stage.querySelector('.cor-contact__stage-canvas');
    if (!canvasWrap) return;

    var raf = null;
    var tx = 0, ty = 0;
    var onMove = function (ev) {
      var w = window.innerWidth;
      var h = window.innerHeight;
      var nx = (ev.clientX / w - 0.5) * 2; // -1..1
      var ny = (ev.clientY / h - 0.5) * 2;
      tx = nx * 12;
      ty = ny * 8;
      if (raf) return;
      raf = requestAnimationFrame(function () {
        canvasWrap.style.transform = 'translate3d(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px, 0)';
        raf = null;
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
  }

  /* ------------------------------------------------------
     7. Belt-and-suspenders reveals
        cor-reveal.js handles [data-cor-reveal], but the
        hero needs to fire even if the user lands deep-scrolled.
        Force-reveal the hero on the first animation frame.
     ------------------------------------------------------ */
  function initHeroForce(root) {
    var hero = root.querySelector('.cor-contact__hero[data-cor-reveal]');
    if (!hero) return;
    // If already revealed (cor-reveal beat us) — done.
    if (hero.classList.contains('is-revealed')) return;
    // If hero is in initial viewport, reveal now (avoid IO race).
    var rect = hero.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) {
      requestAnimationFrame(function () {
        hero.classList.add('is-revealed');
        hero.querySelectorAll('[data-cor-reveal-child]').forEach(function (c) {
          c.classList.add('is-revealed');
        });
      });
    }
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    initFields(root);
    initSubmit(root);
    initScrollSpy(root);
    initCounters(root);
    initSuccessCountdown(root);
    initStage(root);
    initHeroForce(root);
  }

  ready(init);

  // Re-init for Shopify section editor (theme customizer)
  document.addEventListener('shopify:section:load', function (ev) {
    if (ev && ev.target && ev.target.matches && ev.target.matches('.cor-contact, .cor-contact *, [data-section-id]')) {
      init();
    }
  });
})();
